import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';

const CompanyTimeline = () => {
  const [activeYear, setActiveYear] = useState(0);

  const timelineData = [
    {
      year: "1998",
      title: "Fundação da PH Móveis",
      description: "Paulo Henrique inicia sua jornada artesanal em uma pequena oficina em São Paulo, com o sonho de criar móveis únicos que contassem histórias.",
      image: "https://images.unsplash.com/photo-1504148455328-c376907d081c?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "Primeira oficina estabelecida",
      icon: "Home"
    },
    {
      year: "2003",
      title: "Expansão da Equipe",
      description: "Contratação dos primeiros artesãos especializados, estabelecendo os fundamentos da filosofia de trabalho colaborativo e excelência artesanal.",
      image: "https://images.unsplash.com/photo-1581833971358-2c8b550f87b3?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "5 artesãos especializados",
      icon: "Users"
    },
    {
      year: "2008",
      title: "Reconhecimento Nacional",
      description: "Primeiro prêmio de design artesanal brasileiro, consolidando a reputação da marca no mercado de móveis de luxo personalizados.",
      image: "https://images.unsplash.com/photo-1578662996442-48f60103fc96?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "Prêmio Design Brasil 2008",
      icon: "Award"
    },
    {
      year: "2012",
      title: "Sustentabilidade",
      description: "Implementação do programa de madeira certificada e práticas sustentáveis, pioneirismo na preservação ambiental no setor moveleiro.",
      image: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "100% madeira certificada",
      icon: "TreePine"
    },
    {
      year: "2018",
      title: "Showroom Premium",
      description: "Inauguração do showroom de 500m² no coração de São Paulo, oferecendo experiência imersiva completa aos clientes mais exigentes.",
      image: "https://images.unsplash.com/photo-1586023492125-27b2c045efd7?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "Showroom de 500m²",
      icon: "Building"
    },
    {
      year: "2023",
      title: "Inovação Digital",
      description: "Lançamento da plataforma digital de customização 3D, permitindo aos clientes visualizarem suas peças antes da produção.",
      image: "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&auto=format&fit=crop&w=600&q=80",
      milestone: "Tecnologia 3D integrada",
      icon: "Smartphone"
    }
  ];

  return (
    <section className="py-20 bg-muted">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-2 bg-primary/10 text-primary px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Icon name="Clock" size={16} />
            <span>Nossa Jornada</span>
          </div>
          <h2 className="font-playfair text-3xl sm:text-4xl lg:text-5xl font-bold text-primary mb-6">
            25 Anos de Tradição
            <span className="block text-secondary">e Inovação</span>
          </h2>
          <p className="text-lg text-text-secondary max-w-3xl mx-auto">
            Cada marco em nossa história representa um passo em direção à excelência artesanal 
            e ao compromisso com a satisfação de nossos clientes.
          </p>
        </div>

        {/* Interactive Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-border"></div>
          
          {/* Timeline Items */}
          <div className="space-y-12 lg:space-y-20">
            {timelineData?.map((item, index) => (
              <div
                key={index}
                className={`relative transition-all duration-500 ${
                  activeYear === index ? 'opacity-100 scale-100' : 'opacity-70 scale-95'
                }`}
                onMouseEnter={() => setActiveYear(index)}
              >
                <div className={`grid lg:grid-cols-2 gap-8 lg:gap-16 items-center ${
                  index % 2 === 0 ? '' : 'lg:grid-flow-col-dense'
                }`}>
                  {/* Content */}
                  <div className={`${index % 2 === 0 ? '' : 'lg:col-start-2'}`}>
                    <div className="bg-white rounded-2xl p-8 shadow-premium border border-border hover:shadow-premium-lg transition-all duration-300">
                      <div className="flex items-center space-x-4 mb-6">
                        <div className="w-12 h-12 bg-gradient-forest rounded-xl flex items-center justify-center">
                          <Icon name={item?.icon} size={24} color="white" strokeWidth={2} />
                        </div>
                        <div>
                          <div className="text-2xl font-bold text-accent">{item?.year}</div>
                          <div className="text-sm text-text-secondary">{item?.milestone}</div>
                        </div>
                      </div>
                      
                      <h3 className="font-playfair text-2xl font-bold text-primary mb-4">
                        {item?.title}
                      </h3>
                      
                      <p className="text-text-secondary leading-relaxed">
                        {item?.description}
                      </p>
                    </div>
                  </div>

                  {/* Image */}
                  <div className={`${index % 2 === 0 ? '' : 'lg:col-start-1'}`}>
                    <div className="relative overflow-hidden rounded-2xl shadow-premium">
                      <Image
                        src={item?.image}
                        alt={`Marco histórico da PH Móveis em ${item?.year}`}
                        className="w-full h-80 object-cover hover:scale-105 transition-transform duration-500"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
                    </div>
                  </div>
                </div>

                {/* Timeline Dot */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <div className={`w-6 h-6 rounded-full border-4 border-white shadow-premium transition-all duration-300 ${
                    activeYear === index ? 'bg-accent scale-125' : 'bg-border'
                  }`}></div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Dots */}
        <div className="flex justify-center space-x-2 mt-12">
          {timelineData?.map((_, index) => (
            <button
              key={index}
              onClick={() => setActiveYear(index)}
              className={`w-3 h-3 rounded-full transition-all duration-300 ${
                activeYear === index ? 'bg-accent scale-125' : 'bg-border hover:bg-primary/30'
              }`}
              aria-label={`Ver marco do ano ${timelineData?.[index]?.year}`}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default CompanyTimeline;